import { FLOW_TYPE } from '../constants';
import { LISTING_TYPES } from '../../../config/constants';
import { Category } from '../../../data/types/categories';
import { Icon } from '@iconify/react';
import { Colors } from '../../../config/theme';
import { ListingState } from '../../../data/types/listings';
import { Dispatch, useContext } from 'react';
import { ProductListingContext } from './data_formatting_functions';
import {
  actionSetAllAttributes,
  actionSetListingData,
  actionSetProductType,
} from '../store/create_listing_actions';
import { toast } from 'react-toastify';
import { Button } from '@nextui-org/react';

export function getFlowType(isManagement: boolean, location: any) {
  if (isManagement) {
    if (location.state?.duplicate) {
      // Can duplicate existing template (keep template, create new template).
      return FLOW_TYPE.ManagementDuplicateTemplate;
    }
    if (location.state?.row) {
      // Can update existing template (overwrite).
      return FLOW_TYPE.ManagementUpdateTemplate;
    }
    // Can create new template.
    return FLOW_TYPE.ManagementNewTemplate;
  } else {
    if (location?.state?.listing_type_value === LISTING_TYPES.template) {
      if (location?.state?.edit) {
        // Can update existing template (overwrite).
        return FLOW_TYPE.StoreUpdateTemplate;
      } else {
        // Can create new template (add new), create a listing draft from it (keep template, create new draft), or create a new listing from it (keep template, create new listing).
        return FLOW_TYPE.StoreNewListingUsingTemplate;
      }
    } else if (location?.state?.listing_type_value === LISTING_TYPES.draft) {
      // Can update draft (overwrite), create a template from it (keeps draft, creates new template), or create a new listing from it (keep draft, create new listing).
      return FLOW_TYPE.StoreNewListingUsingDraft;
    } else if (
      location?.state?.listing_type_value === LISTING_TYPES.public_listing
    ) {
      if (location?.state?.edit) {
        // Can create new template (add new), create a listing draft from it, or create a new listing.
        return FLOW_TYPE.StoreUpdateListing;
      }
    }
  }
  return FLOW_TYPE.StoreNewListing;
}

export function getAppropriateTitle(flow_type: number) {
  switch (flow_type) {
    case FLOW_TYPE.ManagementNewTemplate:
      return 'Create New Template';
    case FLOW_TYPE.ManagementUpdateTemplate:
      return 'Update Template';
    case FLOW_TYPE.ManagementDuplicateTemplate:
      return 'Duplicate Template';

    case FLOW_TYPE.StoreNewListing:
      return 'Create New Listing';
    case FLOW_TYPE.StoreUpdateListing:
      return 'Update Listing';
    case FLOW_TYPE.StoreNewListingUsingTemplate:
      return 'Create New Listing Using Template';
    case FLOW_TYPE.StoreUpdateTemplate:
      return 'Update Template';
    case FLOW_TYPE.StoreNewListingUsingDraft:
      return 'Create Listing Using Draft';
    default:
      return 'Create New Listing';
  }
}

export function getAppropriateDraftButtonText(flow_type: number) {
  switch (flow_type) {
    // Not applicable for management flows
    case FLOW_TYPE.ManagementNewTemplate:
      return 'N/A';
    case FLOW_TYPE.ManagementUpdateTemplate:
      return 'N/A';
    case FLOW_TYPE.ManagementDuplicateTemplate:
      return 'N/A';

    case FLOW_TYPE.StoreNewListing:
      return 'Save as a draft';
    case FLOW_TYPE.StoreUpdateListing:
      return 'Save as a draft';
    case FLOW_TYPE.StoreNewListingUsingTemplate:
      return 'Save as a new draft';
    case FLOW_TYPE.StoreUpdateTemplate:
      return 'Save listing as a draft';
    case FLOW_TYPE.StoreNewListingUsingDraft:
      return 'Update draft';
    default:
      return 'Save as a draft';
  }
}

export function getAppropriateTemplateButtonText(flow_type: number) {
  switch (flow_type) {
    case FLOW_TYPE.ManagementNewTemplate:
      return 'Publish Template';
    case FLOW_TYPE.ManagementUpdateTemplate:
      return 'Publish Updated Template';
    case FLOW_TYPE.ManagementDuplicateTemplate:
      return 'Publish New Template';

    case FLOW_TYPE.StoreNewListing:
      return 'Save as a template';
    case FLOW_TYPE.StoreUpdateListing:
      return 'Save as a template';
    case FLOW_TYPE.StoreNewListingUsingTemplate:
      return 'Save as a new template';
    case FLOW_TYPE.StoreUpdateTemplate:
      return 'Update template';
    case FLOW_TYPE.StoreNewListingUsingDraft:
      return 'Save as a new template';
    default:
      return 'Save as a template';
  }
}

export function getAppropriatePublishButtonText(flow_type: number) {
  switch (flow_type) {
    // Not applicable for management flows
    case FLOW_TYPE.ManagementNewTemplate:
      return 'N/A';
    case FLOW_TYPE.ManagementUpdateTemplate:
      return 'N/A';
    case FLOW_TYPE.ManagementDuplicateTemplate:
      return 'N/A';

    case FLOW_TYPE.StoreNewListing:
      return 'Request to publish';
    case FLOW_TYPE.StoreUpdateListing:
      return 'Request to update version';
    case FLOW_TYPE.StoreNewListingUsingTemplate:
      return 'Request to publish as listing';
    case FLOW_TYPE.StoreUpdateTemplate:
      return 'Request to publish as listing';
    case FLOW_TYPE.StoreNewListingUsingDraft:
      return 'Request to publish as listing';
    default:
      return 'Request to publish';
  }
}

export function getAppropriateSuccessMessage(type: number, flow_type: number) {
  switch (flow_type) {
    case FLOW_TYPE.ManagementNewTemplate:
      return 'Template has been created successfully';
    case FLOW_TYPE.ManagementUpdateTemplate:
      return 'Template has been updated successfully';
    case FLOW_TYPE.ManagementDuplicateTemplate:
      return 'Template has been duplicated successfully';

    case FLOW_TYPE.StoreNewListingUsingDraft:
      return type === LISTING_TYPES.draft
        ? 'Draft has been updated successfully'
        : type === LISTING_TYPES.template
          ? 'New Template has been created successfully using draft'
          : 'New Listing request has been sent to Silal, using draft';

    case FLOW_TYPE.StoreNewListing:
      return type === LISTING_TYPES.draft
        ? 'New Draft has been saved successfully'
        : type === LISTING_TYPES.template
          ? 'New Template has been created successfully'
          : 'New Listing request has been sent successfully to Silal';
    case FLOW_TYPE.StoreUpdateListing:
      return type === LISTING_TYPES.draft
        ? 'New Draft has been saved successfully'
        : type === LISTING_TYPES.template
          ? 'New Template has been created successfully'
          : 'Listing update request has been sent successfully to Silal';

    case FLOW_TYPE.StoreNewListingUsingTemplate:
      return type === LISTING_TYPES.draft
        ? 'New Draft using this template has been saved successfully'
        : type === LISTING_TYPES.template
          ? 'New Template using this template has been created successfully'
          : 'New Listing request has been sent successfully to Silal, using this template';
    case FLOW_TYPE.StoreUpdateTemplate:
      return 'Template has been updated successfully';
  }
}

export function GetAppropriateFlowButtons(
  requestPublish: (type: number) => void,
  flow: number,
  isManagement: boolean,
  submitting: boolean,
) {
  const {
    state,
    dispatch,
  }: {
    state: ListingState;
    dispatch: any;
  } = useContext(ProductListingContext);
  // Management has only template (middle) button
  // Store has all three buttons always, except for StoreUpdateTemplate (only template button)
  return (
    <div className="d-flex align-items-center">
      {/* <Icon
        icon="akar-icons:save"
        style={{
          width: '30px',
          height: '30px',
          background: Colors.greenMain,
          color: Colors.white,
          fontSize: '20px',
          padding: '5px',
          borderRadius: '20%',
        }}
        onClick={() => {
          if (
            window.confirm(
              'Are you sure you want to save the data locally? This will overwrite any previously saved data.',
            )
          ) {
            localStorage.setItem('listingState', JSON.stringify(state));
            toast.success('Data has been saved locally successfully');
          }
        }}
      />
      <Icon
        icon="akar-icons:file"
        style={{
          background: Colors.greenMain,
          color: Colors.white,
          fontSize: '20px',
          marginLeft: '10px',
          marginRight: '10px',
          padding: '5px',
          width: '30px',
          height: '30px',
          borderRadius: '20%',
        }}
        onClick={() => {
          if (localStorage.getItem('listingState') === null) {
            toast.warn('No locally saved data found');
            return;
          }
          if (
            window.confirm(
              'Are you sure you want to load the current locally saved data? This will overwrite your current form data.',
            )
          ) {
            const listingState: ListingState = JSON.parse(
              localStorage.getItem('listingState') || '{}',
            );
            dispatch(actionSetListingData(listingState.data));
            dispatch(actionSetProductType(listingState.productType));
            dispatch(actionSetAllAttributes(listingState.all_attributes));
          }
        }}
      /> */}

      {/* {!isManagement && flow !== FLOW_TYPE.StoreUpdateTemplate && (
        <Button
          className="mx-3 hover-clr-white"
          onClick={() => requestPublish(LISTING_TYPES.draft)}
          style={{
            backgroundColor: isManagement ? '' : '#05AE4B33',
            color: isManagement ? '' : '#05AE4B',
          }}
        >
          {getAppropriateDraftButtonText(flow)}
        </Button>
      )} */}
      <Button
        className="hover-clr-white"
        onClick={() => requestPublish(LISTING_TYPES.template)}
        isDisabled={submitting}
        isLoading={submitting}
        color={'success'}
        variant={isManagement ? 'solid' : 'flat'}
      >
        {getAppropriateTemplateButtonText(flow)}
      </Button>
      {!isManagement && flow !== FLOW_TYPE.StoreUpdateTemplate && (
        <Button
          isDisabled={submitting}
          isLoading={submitting}
          color="success"
          className="mx-3 hover-clr-white"
          onClick={() => requestPublish(LISTING_TYPES.public_listing)}
        >
          {getAppropriatePublishButtonText(flow)}
        </Button>
      )}
    </div>
  );
}

export function getCategoryFee(
  price: number,
  self: Category,
  categoriesList: Category[],
) {
  if (!self?.silal_fee_s0_percentage) {
    return getCategoryFee(
      price,
      categoriesList.find((c) => c.id === self.parent_category_id)!,
      categoriesList,
    );
  }

  if (
    !self?.silal_fee_s1_bound ||
    price < parseFloat(self?.silal_fee_s1_bound)
  ) {
    return parseFloat(self.silal_fee_s0_percentage!) * price;
  }

  const max_s0_fee =
    parseFloat(self?.silal_fee_s1_bound) *
    parseFloat(self.silal_fee_s0_percentage!);

  if (
    !self?.silal_fee_s2_bound ||
    price < parseFloat(self?.silal_fee_s2_bound)
  ) {
    const s1_fee =
      parseFloat(self.silal_fee_s1_percentage!) *
      (price - parseFloat(self?.silal_fee_s1_bound));
    return max_s0_fee + s1_fee;
  }

  const max_s1_fee =
    (parseFloat(self?.silal_fee_s2_bound) -
      parseFloat(self?.silal_fee_s1_bound)) *
    parseFloat(self.silal_fee_s1_percentage!);

  if (
    !self?.silal_fee_s3_bound ||
    price < parseFloat(self?.silal_fee_s3_bound)
  ) {
    const s2_fee =
      parseFloat(self.silal_fee_s2_percentage!) *
      (price - parseFloat(self?.silal_fee_s2_bound));
    return max_s0_fee + max_s1_fee + s2_fee;
  }

  const max_s2_fee =
    (parseFloat(self?.silal_fee_s3_bound) -
      parseFloat(self?.silal_fee_s2_bound)) *
    parseFloat(self.silal_fee_s2_percentage!);

  const s3_fee =
    parseFloat(self.silal_fee_s3_percentage!) *
    (price - parseFloat(self?.silal_fee_s3_bound));

  return max_s0_fee + max_s1_fee + max_s2_fee + s3_fee;
}
